import type jsPDF from 'jspdf';
import type { Form10ClientParams } from './pdf-types.js';
import { newPDF, placeImage, formatPDFDate, GREEN, type InspectionRow } from './pdf-layout.js';
import { FORM10_ICON_CERT_NO, FORM10_ICON_LICENSE_NO, FORM10_ICON_REGISTRATION_NO, FORM10_ICON_DATE, FORM10_ICON_SIG_USER, FORM10_ICON_COMPETENT_PERSON, FORM10_ICON_COMPETENCY_CERT, FORM10_ICON_VALIDITY, FORM10_ICON_ISSUING_AUTHORITY, FORM10_ICON_NEXT_INSPECTION } from './form10Assets.js';

// A4 geometry measured from the original Canva design DAHT8HurRjU, in mm.
const LEFT = 9.74, RIGHT = 200.3, NUMBER_W = 10.01, LABEL_X = 23.06;
const DIVIDER_X = 106, VALUE_X = 109, TABLE_TOP = 57.46, PAGE_BOTTOM = 288.51;
const ROW_HEIGHTS = [11.91, 11.91, 20.37, 16.14, 16.14, 24.61, 24.61, 24.61];
const LEADING = 4.23;
type Weight = 'normal' | 'semibold' | 'bold';

/** Live text and vector rules retain sharp print output at any resolution. */
export function renderForm10(data: Form10ClientParams, rows: InspectionRow[], statement: string): jsPDF {
  const doc = newPDF(`Form 10 - ${data.certificate_number}`);
  const wrap = (value: string, width: number, size: number, weight: Weight = 'normal'): string[] => {
    doc.setFont('Inter', weight); doc.setFontSize(size);
    return doc.splitTextToSize((value || '-').replace(/[\u2010-\u2015]/g, '-'), width);
  };
  const text = (value: string | string[], x: number, y: number, size: number, weight: Weight = 'normal', leading = size * 25.4 / 72 * 4 / 3) => {
    doc.setFont('Inter', weight); doc.setFontSize(size);
    doc.text(value, x, y, { baseline: 'top', lineHeightFactor: leading / (size * 25.4 / 72) });
  };
  const header = () => {
    doc.setTextColor(...GREEN);
    text('Form No. 10', LEFT, 10.2, 28, 'bold');
    text('(Prescribed Under Rule 60)', LEFT, 23, 11);
    doc.setFillColor(...GREEN); doc.rect(LEFT, 32.26, 11.25, 1.06, 'F');
    doc.setTextColor(96, 160, 28);
    text(['Prescribed For Report of Examination of the', 'Lifting Machines, Ropes and Lifting Tackles'], LEFT, 40, 10.5, 'bold', 5.29);
    doc.setDrawColor(...GREEN); doc.setLineWidth(0.26); doc.line(97.04, 9.99, 97.04, 49.44);
    let metaY = 9.42;
    const metadata = [
      ['License Number', data.company?.license_number, FORM10_ICON_LICENSE_NO],
      ['Certificate No.', data.certificate_number, FORM10_ICON_CERT_NO],
      ['Registration No.', data.company?.registration_number, FORM10_ICON_REGISTRATION_NO],
      ['Date', formatPDFDate(data.examination_date), FORM10_ICON_DATE],
    ];
    metadata.forEach(([label, value, icon], i) => {
      const lines = wrap(value || '-', RIGHT - 148, 8.5, i === 1 ? 'semibold' : 'normal');
      const h = Math.max(10.8, lines.length * 3.8 + 3.2);
      placeImage(doc, icon, 101.35, metaY, 7.62, 7.62);
      doc.setTextColor(...GREEN);
      text(label || '', 111.96, metaY + 1.56, 10.5, 'semibold');
      text(':', 143.5, metaY + 1.56, 10.5, 'semibold');
      doc.setTextColor(0, 0, 0);
      text(lines, 148, metaY + 1.9, 8.5, i === 1 ? 'semibold' : 'normal', 3.8);
      if (i < 3) { doc.setDrawColor(...GREEN); doc.setLineWidth(0.15); doc.line(101.35, metaY + h - 1.72, RIGHT, metaY + h - 1.72); }
      metaY += h;
    });
    return Math.max(TABLE_TOP, metaY + 4.84);
  };
  let y = header();
  const measured = rows.map(row => {
    const labels = wrap(row.label, DIVIDER_X - LABEL_X - 3.3, 9, 'semibold');
    const values = wrap(row.value, RIGHT - VALUE_X - 3, 9);
    return { ...row, labels, values, count: Math.max(labels.length, values.length) };
  });
  const cert = wrap(statement, RIGHT - LEFT, 11);
  const person = data.personnel;
  const rejected = ['REJECT', 'DEFECT_FOUND'].includes(data.defect_status);
  const icons = [FORM10_ICON_COMPETENT_PERSON, FORM10_ICON_COMPETENCY_CERT, FORM10_ICON_VALIDITY, FORM10_ICON_ISSUING_AUTHORITY, FORM10_ICON_NEXT_INSPECTION];
  const details = [
    ['Name of Competent Person', person?.full_name],
    ['Competency Certificate No.', person?.competency_cert_number],
    ['Validity', [formatPDFDate(person?.competency_valid_from), formatPDFDate(person?.competency_valid_upto)].filter(Boolean).join(' to ')],
    ['Certificate issued by', person?.issuing_authority],
    ['Next Inspection Date', rejected ? '-' : formatPDFDate(data.next_inspection_date)],
  ].map(([label, value]) => ({ label: label || '', lines: wrap(value || '-', 76, 9) }));
  const detailHeights = details.map(row => Math.max(11.064, row.lines.length * 3.9 + 3.6));
  const cardH = detailHeights.reduce((sum, h) => sum + h, 0);
  const closingH = 7.3 + cert.length * 5.29 + 7.3 + cardH;
  const available = PAGE_BOTTOM - y - closingH;
  // Reclaim blank cell padding before adding a page; never shrink body text.
  const padding = 1.8;
  const minimum = measured.map(row => row.count * LEADING + 2 * padding);
  const desired = minimum.map((h, i) => Math.max(h, ROW_HEIGHTS[i]));
  const minTotal = minimum.reduce((sum, h) => sum + h, 0);
  const desiredTotal = desired.reduce((sum, h) => sum + h, 0);
  const fitsOnePage = minTotal <= available;
  const extraRatio = desiredTotal <= available ? 1 : fitsOnePage ? (available - minTotal) / (desiredTotal - minTotal) : 1;
  const nextPage = () => { doc.addPage(); y = header(); };
  const drawPart = (row: typeof measured[number], offset: number, count: number, h: number, centered: boolean) => {
    doc.setFillColor(...GREEN); doc.rect(LEFT, y, NUMBER_W, h, 'F');
    doc.setDrawColor(0, 0, 0); doc.setLineWidth(0.26);
    doc.rect(LEFT + NUMBER_W, y, RIGHT - LEFT - NUMBER_W, h);
    doc.line(DIVIDER_X, y, DIVIDER_X, y + h);
    doc.setDrawColor(255, 255, 255); doc.line(LEFT, y + h, LEFT + NUMBER_W, y + h);
    doc.setTextColor(255, 255, 255); doc.setFont('Inter', 'bold'); doc.setFontSize(9);
    doc.text(row.number, LEFT + NUMBER_W / 2, y + h / 2, { align: 'center', baseline: 'middle' });
    const labels = row.labels.slice(offset, offset + count), values = row.values.slice(offset, offset + count);
    // Canva vertically centres each cell independently. Continuations stay top aligned.
    doc.setTextColor(...GREEN);
    if (labels.length) text(labels, LABEL_X, y + (centered ? (h - labels.length * LEADING) / 2 : padding), 9, 'semibold');
    doc.setTextColor(0, 0, 0);
    if (values.length) text(values, VALUE_X, y + (centered ? (h - values.length * LEADING) / 2 : padding), 9);
    y += h;
  };
  measured.forEach((row, index) => {
    const h = minimum[index] + (desired[index] - minimum[index]) * extraRatio;
    if (fitsOnePage || y + h <= 282) { drawPart(row, 0, row.count, h, true); return; }
    if (h <= 50) { nextPage(); drawPart(row, 0, row.count, h, true); return; }
    let offset = 0;
    while (offset < row.count) {
      const count = Math.min(row.count - offset, Math.floor((282 - y - 2 * padding) / LEADING));
      if (count < 1) { nextPage(); continue; }
      drawPart(row, offset, count, count * LEADING + 2 * padding, false);
      offset += count;
      if (offset < row.count) nextPage();
    }
  });
  if (y + closingH > PAGE_BOTTOM + 0.01) nextPage();
  const singlePage = doc.getNumberOfPages() === 1;
  const cardY = singlePage ? Math.max(y + closingH - cardH, PAGE_BOTTOM - cardH) : y + closingH - cardH;
  const certY = singlePage ? Math.max(y + 7.3, Math.min(215.31, cardY - 7.3 - cert.length * 5.29)) : y + 7.3;
  doc.setTextColor(0, 0, 0); text(cert, LEFT, certY, 11, 'normal', 5.29);
  doc.setFillColor(244, 248, 239); doc.setDrawColor(...GREEN); doc.setLineWidth(0.26);
  doc.roundedRect(LEFT, cardY, 49.34, cardH, 3, 3, 'FD');
  doc.roundedRect(61.49, cardY, 138.72, cardH, 3, 3, 'FD');
  placeImage(doc, FORM10_ICON_SIG_USER, 13.39, cardY + 3.37, 7.62, 7.62);
  doc.setTextColor(...GREEN);
  text(['Signature of', 'Competent Person'], 22.88, cardY + 3.48, 9, 'semibold', 3.7);
  placeImage(doc, data.personnel_signature_data_url, 13, cardY + 16, 42, 14);
  placeImage(doc, data.personnel_stamp_data_url, 24, cardY + 32, 22, 20);
  let detailY = cardY;
  details.forEach((detail, i) => {
    placeImage(doc, icons[i], 65.31, detailY + 2.26, 6.26, 6.26);
    doc.setTextColor(...GREEN);
    text(detail.label, 73.11, detailY + 3.54, 9, 'semibold');
    text(':', 116.48, detailY + 3.54, 9, 'semibold');
    doc.setTextColor(0, 0, 0); text(detail.lines, 120, detailY + 3.54, 9, 'normal', 3.9);
    detailY += detailHeights[i];
    if (i < 4) { doc.setDrawColor(...GREEN); doc.setLineWidth(0.15); doc.line(65.31, detailY, 196.65, detailY); }
  });
  for (let page = 1; page <= doc.getNumberOfPages(); page++) {
    doc.setPage(page);
    // Keep the original two greens while insetting the band for ordinary A4 printers.
    doc.setFillColor(96, 160, 28); doc.rect(LEFT, 290, RIGHT - LEFT, 2, 'F');
    doc.setFillColor(...GREEN); doc.rect(LEFT, 290, 136, 2, 'F');
    doc.triangle(LEFT + 136, 290, LEFT + 140, 290, LEFT + 138, 292, 'F');
    if (doc.getNumberOfPages() > 1) {
      doc.setTextColor(...GREEN); text(`Page ${page} / ${doc.getNumberOfPages()}${page > 1 ? ' - continued' : ''}`, LEFT, 52.5, 6);
    }
  }
  return doc;
}
